import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogIn, LogOut, Clock, CheckCircle2, AlarmClock, CalendarRange } from "lucide-react";
import api from "../constant/api";
import { toast } from "react-toastify";
import { useMenuPermission } from "../hooks/useMenuPermission";

const MENU_ID = 19;

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Attendance() {
  const { permissions, loading } = useMenuPermission(MENU_ID);
  const email = localStorage.getItem("userEmail");
  const user = JSON.parse(localStorage.getItem("user"));

const user_id = user?.user_id;
const role = user?.role;

const isAdmin = role === "admin" || role === "superadmin";

  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(isAdmin ? [] : null);
  const [month, setMonth] = useState(currentMonth());
  const [lateCutoff, setLateCutoff] = useState("10:00");
  const [busy, setBusy] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const getLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject("Geolocation not supported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        console.log(error);
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

const getLocationOut = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ lat: null, lng: null });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.log(error);
        resolve({ lat: null, lng: null });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });

 const loadData = () => {
  api.get("/Salesman/getTodayAttendance", {
    params: { user_id }
  })
  .then(res => {
    if (res.data.success) {
      setToday(res.data.data);
    }
  });

  api.get("/Salesman/getAttendance", {
    params: {
      user_id,
      role
    }
  })
  .then(res => {
    if (res.data.success) {
      setHistory(res.data.data || []);
    }
  });

  api.get("/Salesman/getLateCutoff")
  .then(res => {
    if (res.data.success) {
      setLateCutoff(
        (res.data.data.late_mark_cutoff || "10:00:00").slice(0,5)
      );
    }
  });
};

const loadSummary = (m) => {
  api.get("/Salesman/getAttendanceSummary", {
    params: {
      user_id,
      role,
      month: m
    }
  })
  .then(res => {
    if (res.data.success) {
      setSummary(res.data.data);
    }
  });
};

  useEffect(() => { loadData(); }, []);
  useEffect(() => { loadSummary(month); }, [month]);


 const handleCheckIn = async () => {
  try {
    setBusy(true);

    const { latitude, longitude } = await getLocation();
   

    const res = await api.post("/Salesman/checkIn", {
      user_id,
      latitude,
      longitude
    });

    if (res.data.success) {
      toast.success("Checked In");
      loadData();
    } else {
      toast.error(res.data.msg);
      console.log(res.data.msg)
    }
  } catch (err) {
    console.log(err);
    toast.error("Location permission denied");
  } finally {
    setBusy(false);
  }
};

const handleCheckOut = async () => {
  setBusy(true);

  try {
    const location = await getLocationOut();

    console.log("Location:", location);

    if (
      location.lat === null ||
      location.lng === null ||
      location.lat === undefined ||
      location.lng === undefined
    ) {
      toast.error("Unable to get your location");
      setBusy(false);
      return;
    }

    const res = await api.post("/Salesman/checkOut", {
      user_id,
      latitude: Number(location.lat),
      longitude: Number(location.lng),
    });

    if (res.data.success) {
      toast.success(res.data.msg);
      loadData();
    } else {
      toast.error(res.data.msg);
    }
  } catch (err) {
    console.log(err);
    toast.error("Check-out failed");
  } finally {
    setBusy(false);
  }
};

  const indexOfLast = currentPage * itemsPerPage;
  const currentRows = history.slice(indexOfLast - itemsPerPage, indexOfLast);
  const totalPages = Math.ceil(history.length / itemsPerPage);

  const fmtTime = (t) => t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 font-medium">Checking permissions...</p>
      </div>
    );
  }

  const summaryRows = isAdmin ? (summary || []) : (summary ? [summary] : []);

  return (
    <div className="p-2 md:p-4">

      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Attendance</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isAdmin ? "Track your team's daily attendance" : "Mark your daily check-in / check-out"}
          </p>
        </div>
        <span className="flex items-center gap-1.5 text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full font-medium self-start sm:self-auto">
          <AlarmClock size={13} /> Late mark after {lateCutoff}
        </span>
      </div>

      {/* CHECK IN/OUT CARD */}
      {!isAdmin && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-lg p-5 mb-5 text-white">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm opacity-80">Today</p>
              <p className="text-lg font-bold">
                {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
              </p>

              {today?.checkin_time && (
                <p className="text-sm mt-2 flex items-center gap-1.5 opacity-90">
                  <CheckCircle2 size={14} /> Checked in at {fmtTime(today.check_in_time)}
                  {today.late_by_minutes > 0 && (
                    <span className="bg-amber-400/90 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full ml-1">
                      LATE {today.late_by_minutes}m
                    </span>
                  )}
                  {today.check_out_time && ` · Out at ${fmtTime(today.check_out_time)}`}
                </p>
              )}
            </div>

            {!today?.checkin_time && (
              <button
                onClick={handleCheckIn}
                disabled={busy}
                className="flex items-center gap-2 bg-white text-blue-700 font-bold px-6 py-3 rounded-xl shadow active:scale-95 transition disabled:opacity-50"
              >
                <LogIn size={18} /> Check In
              </button>
            )}

            {today?.checkin_time && !today?.checkout_time && (
              <button
                onClick={handleCheckOut}
                disabled={busy}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-xl shadow active:scale-95 transition disabled:opacity-50"
              >
                <LogOut size={18} /> Check Out
              </button>
            )}

            {today?.checkout_time && (
              <span className="bg-white/20 px-4 py-2 rounded-xl text-sm font-semibold">
                Day complete ✅
              </span>
            )}
          </div>
        </div>
      )}

      {/* MONTHLY SUMMARY */}
      <div className="bg-white rounded-2xl shadow border border-gray-100 p-4 md:p-5 mb-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <CalendarRange size={17} className="text-blue-600" /> Monthly Summary
          </h2>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {summaryRows.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No attendance data for this month yet.</p>
        ) : (
          <div className="space-y-4">
            {summaryRows.map((s) => (
              <div key={s.admin_id} className="border border-gray-100 rounded-xl p-3">
                {isAdmin && (
                  <p className="font-semibold text-gray-800 text-sm mb-2">
                    {s.name} <span className="text-gray-400 font-normal text-xs">{s.employee_code}</span>
                  </p>
                )}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center">
                  <div className="bg-green-50 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">Present</p>
                    <p className="font-bold text-green-700">{s.present_days}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">Absent</p>
                    <p className="font-bold text-red-700">{s.absent_days}</p>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">Late</p>
                    <p className="font-bold text-amber-700">{s.late_days}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">On Leave</p>
                    <p className="font-bold text-gray-700">{s.leave_days}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2">
                    <p className="text-[10px] text-gray-500">Avg Hrs/Day</p>
                    <p className="font-bold text-blue-700">{s.avg_hours_per_day}h</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block overflow-x-auto bg-white rounded-2xl shadow border border-gray-100">
        <table className="min-w-full text-sm">
          <thead className="bg-gradient-to-r from-gray-900 to-gray-700 text-white">
            <tr>
              {isAdmin && <th className="px-4 py-3 text-left">Salesman</th>}
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-center">Check In</th>
              <th className="px-4 py-3 text-center">Check Out</th>
              <th className="px-4 py-3 text-center">Hours</th>
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((r) => (
              <tr key={r.attendance_id} className="border-b hover:bg-gray-50 transition">
                {isAdmin && <td className="px-4 py-3 font-medium text-gray-800">{r.name}</td>}
                <td className="px-4 py-3">{new Date(r.attendance_date).toLocaleDateString("en-IN")}</td>
                <td className="px-4 py-3 text-center">
                  {fmtTime(r.checkin_time)}
                  {r.late_by_minutes > 0 && (
                    <span className="ml-1.5 bg-amber-100 text-amber-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                      LATE {r.late_by_minutes}m
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">{fmtTime(r.checkout_time)}</td>
                <td className="px-4 py-3 text-center font-semibold text-blue-600">{r.total_hours ? `${Number(r.total_hours).toFixed(1)}h` : "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    r.status === "present" ? "bg-green-100 text-green-700" :
                    r.status === "on_leave" ? "bg-gray-100 text-gray-600" :
                    "bg-yellow-100 text-yellow-700"
                  }`}>
                    {r.status}
                  </span>
                </td>
              </tr>
            ))}
            {currentRows.length === 0 && (
              <tr><td colSpan={isAdmin ? 6 : 5} className="text-center py-10 text-gray-400">No attendance records yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="md:hidden space-y-3">
        {currentRows.map((r) => (
          <motion.div
            key={r.id}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-100 rounded-2xl shadow-sm p-4"
          >
            <div className="flex justify-between items-start">
              <div>
                {isAdmin && <p className="font-semibold text-gray-800 text-sm">{r.name}</p>}
                <p className="text-xs text-gray-500">{new Date(r.attendance_date).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${
                  r.status === "present" ? "bg-green-100 text-green-700" :
                  r.status === "on_leave" ? "bg-gray-100 text-gray-600" :
                  "bg-yellow-100 text-yellow-700"
                }`}>
                  {r.status}
                </span>
                {r.late_by_minutes > 0 && (
                  <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
                    LATE {r.late_by_minutes}m
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div className="bg-gray-50 rounded-xl p-2">
                <p className="text-[10px] text-gray-400">In</p>
                <p className="text-sm font-semibold">{fmtTime(r.checkin_time)}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-2">
                <p className="text-[10px] text-gray-400">Out</p>
                <p className="text-sm font-semibold">{fmtTime(r.checkout_time)}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-2">
                <p className="text-[10px] text-gray-400">Hours</p>
                <p className="text-sm font-semibold text-blue-600">{r.total_hours ? `${Number(r.total_hours).toFixed(1)}h` : "—"}</p>
              </div>
            </div>
          </motion.div>
        ))}
        {currentRows.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <Clock className="mx-auto mb-2" size={28} />
            No attendance records yet.
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <div className="flex flex-wrap justify-center mt-5 gap-2 pb-6">
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1.5 rounded-lg border text-sm ${currentPage === i + 1 ? "bg-blue-500 text-white border-blue-500" : "border-gray-300"}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
